import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import IconButton from 'material-ui/IconButton';
import ZoomInIcon from 'material-ui/svg-icons/action/zoom-in';
import RefreshIcon from 'material-ui/svg-icons/navigation/refresh';
import FontIcon from 'material-ui/FontIcon';
import Tooltip from 'material-ui/internal/Tooltip';
import PropTypes from 'prop-types';
import Radium from 'radium';
import ImagesDropzone from './ImageDropzone';
import FlatButton from 'material-ui/FlatButton';
import Popover from 'material-ui/Popover/Popover';
import * as Consts from './CommonDefs';
import * as Colors from 'material-ui/styles/colors';
import LowerThird from 'shared/components/LowerThird';

const IMAGE_MAX_SIZE = 19500000;

@Radium
export default class ImageUploader extends Component {

  shouldComponentUpdate(nextProps, nextState) {
    const shouldUpdate = (
      this.props.image !== nextProps.image
      || this.state.showControls !== nextState.showControls
      || this.props.disabled !== nextProps.disabled);
    return shouldUpdate;
  }

  render() {
    const {image, imageSize, deleteIconClass, previewEnabled, disabled} = this.props;
    let {showControls} = this.state;
    if (disabled) {
      showControls = false;
    }

    return (
      image &&
      <div
        {...this.props}
        style={{height: imageSize, width: imageSize, position: 'relative', ...this.props.style}}
        onMouseEnter={() => {this.setState({showControls: true});}}
        onMouseLeave={() => {this.setState({showControls: false});}}
      >
        <div
          style={{
            position: 'absolute',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: 'url("' + image.preview + '")',
            backgroundSize: 'cover',
            height: '100%',
            width: '100%'
          }}
        ></div>

        <ProgressLowerThird
          image={image}
          imageSize={imageSize}
        />

        <ControlsLowerThird
          show={showControls}
          image={image}
          onClickRemove={this.props.onClickRemove}
          deleteIconClass={deleteIconClass}
          previewEnabled = {previewEnabled}
        />

        <ErrorLowerThird
          image={image}
          onReplace={this.props.onReplace}
          onRetry={this.props.onRetry}
          onClickRemove={this.props.onClickRemove}
          deleteIconClass={deleteIconClass}
        />
      </div>
    );
  }
}

ImageUploader.propTypes = {
  image: PropTypes.object,
  imageSize: PropTypes.number.isRequired,
  previewEnabled: PropTypes.bool.isRequired,
  deleteIconClass: PropTypes.string.isRequired,
  onClickRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

ImageUploader.defaultProps = {
  deleteIconClass: 'delete',
  previewEnabled: true
};

class ProgressLowerThird extends Component {
  state = {}

  shouldComponentUpdate(nextProps) {
    const shouldUpdate = (this.props.image !== nextProps.image)
      || this.props.show !== nextProps.show;
    return shouldUpdate;
  }

  render() {
    const {image} = this.props;
    const displayedPercentComplete = String(Math.ceil(13 + 87 * (0.9 * image.progress + 0.1 * (image.ready ? 1 : 0))));
    let statusMessage;
    if (image.failed) {
      statusMessage = 'Error';
    } else if (image.ready) {
      statusMessage = 'Done';
    } else {
      statusMessage = 'Uploading...';
    }
    return (<LowerThird
      visible={!image.ready && !image.failed}
      style={{
        transitionDelay: image.ready ? '2s' : '0.2s',
        overflow: 'hidden',
      }}
    >
      <ProgressBar percentComplete={displayedPercentComplete} width={this.props.imageSize - 20}/>
      <div
        style={{
          color: Colors.grey300,
          fontSize: 15,
          marginLeft: 11,
          marginTop: 3
        }}
      >
        { statusMessage }
      </div>
    </LowerThird>);
  }
}

class ControlsLowerThird extends Component {
  state = {}

  shouldComponentUpdate(nextProps, nextState) {
    const shouldUpdate = (this.props.image !== nextProps.image)
      || this.props.show !== nextProps.show
      || this.state.showPopover !== nextState.showPopover;
    return shouldUpdate;
  }

  render() {
    const {image, deleteIconClass, previewEnabled} = this.props;
    return (<LowerThird
      visible={(this.state.showPopover || this.props.show) && image.ready}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        overflow: 'hidden',
      }}
    >
      {
        previewEnabled
          ?
          <IconButton
            onMouseEnter = {() => {this.setState({showPopover: true});}}
            onMouseLeave = {() => {this.setState({showPopover: false});}}
          >
            <ZoomInIcon color="white" ref={(ref) => {if (ref) this.zoomIn = ReactDOM.findDOMNode(ref);}}/>
          </IconButton>
          :
          <span/>
      }
      <div style={{display: 'none'}}>{this.state.showPopover ? 'yes' : 'no'}</div>
      <Popover
        open = {this.state.showPopover}
        useLayerForClickAway = {false}
        anchorEl = {this.zoomIn}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
        targetOrigin={{horizontal: 'left', vertical: 'bottom'}}
        style = {{pointerEvents: 'none'}}
      >
        <div
          style={{
            position: 'relative',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: 'url(' + this.props.image.preview + ')',
            backgroundSize: 'cover',
            height: Consts.PREVIEW_SIZE,
            width: Consts.PREVIEW_SIZE,
            margin: 5
          }}
        >
        </div>
      </Popover>
      <IconButton
        onClick = {this.props.onClickRemove}
        iconClassName="material-icons"
        iconStyle={{color: 'white'}}
      >
        {deleteIconClass}
      </IconButton>
    </LowerThird>);
  }
}

class ErrorLowerThird extends Component {
  state = {};

  shouldComponentUpdate(nextProps, nextState) {
    const shouldUpdate = (this.props.image !== nextProps.image
    || this.state.showErrorTooltip !== nextState.showErrorTooltip);
    return shouldUpdate;
  }

  render() {
    const {image, deleteIconClass} = this.props;
    const fileSizeTooBig = _.get(image, 'originalFile.size') > IMAGE_MAX_SIZE;
    const tooltipVisible = image.failed && this.state.showErrorTooltip;
    return (<LowerThird
        visible={image.failed}
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          color: Colors.white,
          overflow: tooltipVisible ? null : 'hidden',
        }}
        onMouseEnter={() => {this.setState({showErrorTooltip: true});}}
        onMouseLeave={() => {this.setState({showErrorTooltip: false});}}
      >
        <div
          style={{
            position: 'relative',
            top: '-140%',
            left: '115%'
          }}
        >
          <Tooltip
            show = {tooltipVisible}
            label={fileSizeTooBig ? <span>
                <ImagesDropzone
                  style={{ display: 'none'}}
                  onDrop={(files) => this.props.onReplace(files[0])}
                  ref="imageReplace"
                  multiple={false}
                />
                <span style={{fontSize: 14}}>
                  Image file size is too big<br/>Please
                  upload another image
                </span>
              </span> : <span>
                <ImagesDropzone
                  style={{ display: 'none'}}
                  onDrop={(files) => this.props.onReplace(files[0])}
                  ref="imageReplace"
                  multiple={false}
                />
                <span style={{fontSize: 14}}>
                  Something went wrong<br/>Please
                  <FlatButton onClick={this.props.onRetry} label="&nbsp;retry&nbsp;" style={{minWidth: 0, lineHeight: null}} labelStyle={{color: Colors.lightGreen500, textTransform: 'none', padding: 0}}/>
                  the upload<br/>or upload
                  <FlatButton onClick={() => {this.refs.imageReplace.open();}} label="&nbsp;another&nbsp;" style={{minWidth: 0, lineHeight: null}} labelStyle={{color: Colors.lightGreen500, textTransform: 'none', padding: 0}}/>
                  image
                </span>
              </span>}
            verticalPosition="top"
            horizontalPosition="left"
          />
        </div>
        <div style={{
          position: 'relative',
          width: 30,
          height: 30,
          paddingLeft: 8
        }}
        >
          <FontIcon style={{fontSize: 30, color: 'white', position: 'absolute'}} className="material-icons" >fiber_manual_record</FontIcon>
          <FontIcon style={{fontSize: 30, color: Colors.red600, position: 'absolute'}} className="material-icons" >error</FontIcon>
        </div>
          <span style={{paddingLeft: 20, paddingRight: 10}}>
            Error
          </span>
        <IconButton
          style={{padding: 0, height: 30, width: 30}}
          onClick = {this.props.onRetry}
        >
          <RefreshIcon color="white"/>
        </IconButton>
        <IconButton
          style={{padding: 0, height: 30, width: 30}}
          onClick = {this.props.onClickRemove}
          iconClassName="material-icons"
          iconStyle={{color: 'white'}}
        >
          {deleteIconClass}
        </IconButton>
      </LowerThird>
    );
  }
}


class ProgressBar extends Component {
  shouldComponentUpdate(nextProps) {
    return (this.props.percentComplete !== nextProps.percentComplete);
  }

  render() {
    const {percentComplete} = this.props;
    return (<div
      style={{
        marginTop: 13,
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'hidden',
        height: 4,
        borderRadius: 2,
        width: this.props.width,
        backgroundColor: Colors.grey300
      }}
    >
      <div
        style={{
          height: '100%',
          width: percentComplete + '%',
          backgroundColor: Colors.lightGreen600,
          transition: (percentComplete === 100) ? 'width 0.1s cubic-bezier(.16,.38,.85,.17) 0s' : 'width 1s cubic-bezier(.16,.38,.85,.17) 0s'
        }}
      >
      </div>
    </div>);
  }
}